/**
 * safeInternalPath — the single chokepoint for post-auth "return URL" navigation.
 *
 * Post-login flows read a `returnUrl` from the query string, localStorage, or an
 * OAuth `state` blob and then navigate to it. Those values are attacker-influenceable,
 * so an unsanitised return path is a DOM-XSS / open-redirect primitive:
 *   - `javascript:alert(1)` / `data:...` assigned to `window.location.href` executes script.
 *   - `//evil.example`, `/\evil.example`, `https://evil.example` redirect off-origin
 *     AFTER auth tokens are already in localStorage (token-theft primitive).
 *
 * This helper collapses every return-path decision into ONE tested rule set:
 *   - Only same-origin `http:`/`https:` targets survive.
 *   - The result is ALWAYS a relative path (`pathname + search + hash`) — never an
 *     absolute URL — so a rejected value can never leak an off-origin destination.
 *   - Auth routes are blocked to prevent post-login redirect loops.
 * Anything missing, malformed, or unsafe collapses to the caller-supplied `fallback`.
 *
 * The expected application origin is `window.location.origin` (self-origin), read at
 * call time. This is authoritative in the browser and avoids depending on a possibly
 * stale VITE_APP_URL env value. In jsdom tests it is the mocked test origin.
 */

/** Default post-login landing used as the fallback at most call sites. */
export const DEFAULT_SAFE_PATH = '/mission-control';

/**
 * Public auth routes. A return path that lands on one of these would bounce the
 * just-authenticated user straight back into the login flow, so they are rejected.
 */
const LOOP_PATHS = ['/login', '/register', '/forgot-password', '/reset-password'];

/**
 * Sanitise an untrusted return-path value into a safe, same-origin relative path.
 *
 * @param raw The untrusted candidate (query param, localStorage value, OAuth state, …).
 * @param fallback The safe path to return when `raw` is missing/malformed/unsafe.
 * @returns A relative internal path (`/…`) or `fallback`. Never an absolute URL, never
 *          the raw hostile string.
 */
export function safeInternalPath(raw: string | null | undefined, fallback: string): string {
  if (typeof raw !== 'string') {
    return fallback;
  }

  // Mirror the WHATWG URL parser's own input cleaning so obfuscated schemes cannot slip
  // past the protocol/scheme check: the parser removes tab/newline/CR from anywhere in
  // the input, then strips surrounding whitespace. Doing it up front means our own scheme
  // sniff below sees exactly what the parser will act on. Filtering by charCode (not a
  // control-char regex) keeps this lint-clean under `no-control-regex`.
  let stripped = '';
  for (const ch of raw) {
    const code = ch.charCodeAt(0);
    if (code === 0x09 || code === 0x0a || code === 0x0d) {
      continue; // strip TAB / LF / CR wherever they appear
    }
    stripped += ch;
  }
  const cleaned = stripped.trim();

  if (!cleaned) {
    return fallback;
  }

  // A legitimate internal reference is a path/query/fragment (starts with `/`, `?`, `#`)
  // or an absolute same-origin http(s) URL. Anything carrying a scheme-colon BEFORE its
  // first slash — an opaque scheme like `mailto:` / `:::`, or a percent-encoded scheme
  // (`%6a%61vascript:` → the parser treats it as a path segment, not a scheme) — is
  // rejected outright. We do NOT substring-scan for scheme names; we only reject the
  // structural "colon before first slash" shape that a real internal path never has.
  const isAbsoluteHttp = /^https?:\/\//i.test(cleaned);
  if (!isAbsoluteHttp) {
    const firstColon = cleaned.indexOf(':');
    const firstSlash = cleaned.indexOf('/');
    if (firstColon !== -1 && (firstSlash === -1 || firstColon < firstSlash)) {
      return fallback;
    }
  }

  const origin = window.location.origin;

  let url: URL;
  try {
    url = new URL(cleaned, origin);
  } catch {
    // Malformed input (e.g. `http://`) — reject. Do NOT log `raw`; it may be hostile.
    return fallback;
  }

  // Permit only http(s). The parser resolves `javascript:` / `data:` / `file:` / `blob:`
  // etc. to their real protocol regardless of case or embedded tab/newline obfuscation.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return fallback;
  }

  // Must resolve to our own origin EXACTLY. The WHATWG parser normalises `\` to `/`, so
  // `//evil`, `/\evil`, `\/\/evil` and absolute foreign URLs all resolve to a foreign
  // origin here and get rejected.
  if (url.origin !== origin) {
    return fallback;
  }

  // Never bounce back into an auth route (redirect-loop guard). Matches the legacy
  // startsWith blocklist this helper replaces.
  if (LOOP_PATHS.some(p => url.pathname.startsWith(p))) {
    return fallback;
  }

  // Return ONLY the relative internal parts — never the absolute URL.
  return url.pathname + url.search + url.hash;
}
